const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;
const BUSINESS_TIME_ZONE = "Europe/Amsterdam";

function parseIsoDateOnly(value) {
  if (typeof value !== "string" || !ISO_DATE_ONLY.test(value)) return null;
  const milliseconds = Date.parse(`${value}T00:00:00Z`);
  if (!Number.isFinite(milliseconds)) return null;
  return new Date(milliseconds).toISOString().slice(0, 10) === value
    ? milliseconds
    : null;
}

/**
 * Canonical value for one date field that ships under two API casings.
 *
 * `value` prefers the snake_case (primary) reading and falls back to the
 * camelCase (legacy) alias, so a field stays usable even when the producer
 * sends both casings with different days. `conflict` reports that drift so
 * callers that make a COMMERCIAL promise on top of the field can fail closed
 * on the flag itself, instead of relying on a null value to stop them.
 *
 * `failClosedOnConflict` additionally nulls the value: reserved for a field
 * that is reproduced verbatim as a promise (the sale cutoff), where there is
 * no honest reading to prefer.
 */
function resolveAliasedIsoDate(primary, alias, { failClosedOnConflict = false } = {}) {
  const primaryValid = parseIsoDateOnly(primary) != null;
  const aliasValid = parseIsoDateOnly(alias) != null;
  const conflict =
    primaryValid && aliasValid && String(primary) !== String(alias);
  const preferred = primaryValid ? primary : aliasValid ? alias : null;
  return {
    value: conflict && failClosedOnConflict ? null : preferred,
    conflict,
  };
}

function businessDateValues(now) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: BUSINESS_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  return Object.fromEntries(parts.map(({ type, value }) => [type, value]));
}

function businessTodayMs(now) {
  const values = businessDateValues(now);
  return Date.UTC(Number(values.year), Number(values.month) - 1, Number(values.day));
}

function businessDateKey(now) {
  const values = businessDateValues(now);
  return `${values.year}-${values.month}-${values.day}`;
}

/**
 * Today's Europe/Amsterdam calendar day as a bare `YYYY-MM-DD`.
 *
 * The business day is already the unit every gate in this module compares
 * against (`businessTodayMs`); this exposes the same value in the ISO
 * date-only form so a caller that holds a date STRING (a feed cutoff, a
 * JSON-LD date) can compare it directly, lexicographically, without either
 * re-implementing the timezone lookup or round-tripping through `Date` in
 * the server's own timezone. Returns null for an unusable clock.
 */
export function amsterdamBusinessDate(now = new Date()) {
  if (!(now instanceof Date) || Number.isNaN(now.getTime())) return null;
  return businessDateKey(now);
}

/** Milliseconds until Europe/Amsterdam moves to its next calendar day. */
export function millisecondsUntilAmsterdamDateChange(now = new Date()) {
  if (!(now instanceof Date) || Number.isNaN(now.getTime())) return 60_000;
  const startMs = now.getTime();
  const currentKey = businessDateKey(now);
  let low = startMs;
  let high = startMs + 27 * 60 * 60 * 1000;
  if (businessDateKey(new Date(high)) === currentKey) return 60_000;

  // Binary-search the timezone-aware date boundary. This remains correct on
  // both 23-hour and 25-hour DST days without depending on the browser's own
  // local timezone.
  while (high - low > 1) {
    const middle = Math.floor((low + high) / 2);
    if (businessDateKey(new Date(middle)) === currentKey) low = middle;
    else high = middle;
  }
  return Math.max(1, high - startMs);
}

function localizedDateLabel(value, locale) {
  const milliseconds = parseIsoDateOnly(value);
  if (milliseconds == null) return null;
  const localeTag = {
    nl: "nl-NL",
    de: "de-DE",
    en: "en-GB",
    fr: "fr-FR",
    pl: "pl-PL",
  }[locale] || "nl-NL";
  return new Intl.DateTimeFormat(localeTag, {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(milliseconds));
}

/** Canonical date-field normalization for mixed BFF/CMS payload shapes. */
export function resolveDealDateFields(deal) {
  if (!deal || typeof deal !== "object") {
    return {
      travelStartDate: null,
      travelEndDate: null,
      bookingEndDate: null,
      hasDateFieldConflict: false,
      hasTravelDateFieldConflict: false,
      hasBookingEndDateFieldConflict: false,
    };
  }
  const travelStart = resolveAliasedIsoDate(
    deal.travel_start_date,
    deal.travelStartDate,
  );
  const travelEnd = resolveAliasedIsoDate(
    deal.travel_end_date,
    deal.travelEndDate,
  );
  const bookingEnd = resolveAliasedIsoDate(
    deal.booking_end_date,
    deal.bookingEndDate,
    { failClosedOnConflict: true },
  );
  return {
    travelStartDate: travelStart.value,
    travelEndDate: travelEnd.value,
    bookingEndDate: bookingEnd.value,
    // Conflicting valid aliases are evidence of contract drift, reported to
    // every caller through these flags. Commercial gates fail closed on the
    // flags until the producer sends one unambiguous date.
    //
    // The travel window keeps its preferred reading anyway, because it is a
    // QUERY RANGE, not a promise: the calendar/lowest-price consumers feed it
    // to a per-date price lookup that can only ever offer dates the backend
    // actually prices, and dropping it to null made an ordinary flexible deal
    // unusable (empty BFF range, no travel-period copy) instead of safer.
    // `booking_end_date` is the opposite shape — it is reproduced verbatim as
    // a sale-cutoff promise (JSON-LD validThrough, feed expiration_date,
    // checkout "boek uiterlijk") — so it stays null while its aliases drift.
    hasDateFieldConflict:
      travelStart.conflict || travelEnd.conflict || bookingEnd.conflict,
    hasTravelDateFieldConflict: travelStart.conflict || travelEnd.conflict,
    hasBookingEndDateFieldConflict: bookingEnd.conflict,
  };
}

/**
 * A zero-to-two-day travel window represents one fixed arrival date.
 *
 * Both shapes occur in Favotrip data: some producers model the final date as
 * inclusive (start === end), while the public deal API normally models the
 * end as an exclusive checkout boundary. A two-night extension can therefore
 * end two days after arrival; longer ranges remain ordinary flexible windows.
 * This matches the backend fixed-date booking-window contract.
 */
export function isFixedArrivalWindow(startDate, endDate) {
  const startMs = parseIsoDateOnly(startDate);
  const endMs = parseIsoDateOnly(endDate);
  if (startMs == null || endMs == null) return false;
  const spanDays = (endMs - startMs) / DAY_MS;
  return Number.isInteger(spanDays) && spanDays >= 0 && spanDays <= 2;
}

function validAliasedDateCandidates(primary, alias) {
  return [...new Set([primary, alias].filter((value) => parseIsoDateOnly(value) != null))];
}

function hasPlausibleFixedArrivalAliasCombination(deal) {
  const starts = validAliasedDateCandidates(
    deal?.travel_start_date,
    deal?.travelStartDate,
  );
  const ends = validAliasedDateCandidates(
    deal?.travel_end_date,
    deal?.travelEndDate,
  );
  return starts.some((start) =>
    ends.some((end) => isFixedArrivalWindow(start, end)),
  );
}

export function isFixedDateVoucher(deal) {
  const { travelStartDate, travelEndDate, hasTravelDateFieldConflict } =
    resolveDealDateFields(deal);
  // Conflicting travel aliases can never ADVERTISE a fixed campaign: the
  // canonical value is only the preferred reading of drifted data, not proof
  // of one event date, and this predicate gates the arrival label, the
  // fixed-arrival calendar mode and the expiry claim. Purchase safety for
  // such a row is decided separately by `isFixedDateVoucherUnavailable`,
  // which fails closed on every plausible fixed alias combination.
  if (hasTravelDateFieldConflict) return false;
  return isFixedArrivalWindow(travelStartDate, travelEndDate);
}

/** A genuine Oud & Nieuw product: one fixed arrival specifically on 31 Dec. */
export function isNewYearsEveFixedDateCampaign(deal) {
  if (!isFixedDateVoucher(deal)) return false;
  const { travelStartDate } = resolveDealDateFields(deal);
  return typeof travelStartDate === "string" && travelStartDate.endsWith("-12-31");
}

function lengthOfStayOptions(raw) {
  let values = raw;
  if (typeof values === "string") {
    const trimmed = values.trim();
    if (!trimmed) return [];
    try {
      values = JSON.parse(trimmed);
    } catch {
      values = trimmed.includes(",") ? trimmed.split(",") : [trimmed];
    }
  }
  if (!Array.isArray(values)) values = [values];
  return [
    ...new Set(
      values
        .map(Number)
        .filter((value) => Number.isInteger(value) && value >= 2),
    ),
  ];
}

/**
 * True only when redemption explicitly offers both LOS 2 and LOS 3, i.e.
 * one base night plus an optional second night in this booking model.
 */
export function fixedDateVoucherHasOptionalSecondNight(deal) {
  if (!isFixedDateVoucher(deal)) return false;
  const snakePresent = deal?.length_of_stay !== undefined && deal?.length_of_stay !== null;
  const camelPresent = deal?.lengthOfStay !== undefined && deal?.lengthOfStay !== null;
  const snakeOptions = snakePresent
    ? lengthOfStayOptions(deal.length_of_stay)
    : [];
  const camelOptions = camelPresent
    ? lengthOfStayOptions(deal.lengthOfStay)
    : [];

  // This helper drives a public “extend to two nights” promise. When both
  // API aliases are present, only make that promise if both are valid and
  // semantically agree; silently preferring snake_case could otherwise turn
  // producer drift such as [2,3] versus [2] into false availability copy.
  if (snakePresent && camelPresent) {
    const snakeCanonical = [...snakeOptions].sort((a, b) => a - b);
    const camelCanonical = [...camelOptions].sort((a, b) => a - b);
    if (
      snakeCanonical.length === 0 ||
      snakeCanonical.length !== camelCanonical.length ||
      snakeCanonical.some((value, index) => value !== camelCanonical[index])
    ) {
      return false;
    }
  }

  const options = snakePresent ? snakeOptions : camelOptions;
  return options.includes(2) && options.includes(3);
}

/**
 * Whether the fixed arrival day has already passed in Amsterdam business time.
 *
 * The API models a one-night stay as 31 Dec -> 1 Jan (exclusive checkout).
 * A new booking is already impossible on 1 Jan because the arrival was the
 * previous day, so expiry is deliberately based on travel_start_date.
 */
export function isFixedDateVoucherExpired(deal, now = new Date()) {
  if (!isFixedDateVoucher(deal)) return false;
  const { travelStartDate: value } = resolveDealDateFields(deal);
  const startMs = parseIsoDateOnly(value);
  if (startMs == null || !(now instanceof Date) || Number.isNaN(now.getTime())) {
    return false;
  }
  const todayMs = businessTodayMs(now);
  return startMs < todayMs;
}

/** Purchase availability for a fixed-date campaign, including its sale cutoff. */
export function isFixedDateVoucherUnavailable(deal, now = new Date()) {
  const dateFields = resolveDealDateFields(deal);
  if (dateFields.hasTravelDateFieldConflict) {
    // Conflicting aliases are unsafe only when at least one valid producer
    // interpretation can describe a zero/one-day fixed campaign. If every
    // valid combination is a longer flexible window, hiding the row would be
    // an ordinary-catalogue regression rather than a fail-closed safeguard.
    return hasPlausibleFixedArrivalAliasCombination(deal);
  }
  if (
    !isFixedArrivalWindow(
      dateFields.travelStartDate,
      dateFields.travelEndDate,
    )
  ) {
    // A known live campaign whose slim payload omitted the authoritative
    // travel window must never fall through as an ordinary rolling voucher.
    // Identity can only make a row unavailable; it can never prove a sale
    // open. Clearing the cohort below retires this last-resort safeguard.
    return isKnownFixedDateCampaignId(
      deal?.deals_id ?? deal?.dealId,
    );
  }
  if (dateFields.hasBookingEndDateFieldConflict) return true;
  if (isFixedDateVoucherExpired(deal, now)) return true;
  if (!(now instanceof Date) || Number.isNaN(now.getTime())) return false;
  const { bookingEndDate: bookingEnd } = dateFields;
  const bookingEndMs = parseIsoDateOnly(bookingEnd);
  // A fixed sale without a valid explicit cutoff is not safe to sell. Never
  // silently substitute the arrival day: that would turn a missing/malformed
  // campaign field into a longer commercial promise than the operator set.
  if (bookingEndMs == null) return true;
  const todayMs = businessTodayMs(now);
  return bookingEndMs < todayMs;
}

/** Remove only unavailable fixed-event campaigns; ordinary rows are retained. */
export function filterAvailableFixedDateCampaigns(rows, now = new Date()) {
  if (!Array.isArray(rows)) return [];
  return rows.filter((deal) => !isFixedDateVoucherUnavailable(deal, now));
}

/**
 * The deal ids of the fixed-date campaign cohort that is live right now.
 *
 * This list is the ONLY place the cohort is written down. It lives in this
 * module — not in `edge-cache.js`, where it started — because two layers that
 * cannot see a deal row need it and both already import from here: the edge
 * (middleware sees a path and a query, never the resolved row) and the
 * calendar BFF route (which needs a verdict for a request whose authoritative
 * row could not be fetched at all). Duplicating the ids per layer is how one
 * of them silently goes stale when the cohort is retired.
 *
 * Deliberately NOT the general gate: for every surface that HAS the deal row,
 * `isFixedDateVoucherUnavailable` decides, and it needs no id list. This set
 * only answers "is this id known to be a fixed campaign", which is a weaker
 * and narrower question — see `isKnownFixedDateCampaignId`.
 *
 * Retiring the campaign is a one-line edit: empty the set. Every consumer
 * (the edge boundary clamp, the calendar route's no-authority posture) then
 * returns to its ordinary behaviour with no other change.
 */
const FIXED_DATE_CAMPAIGN_DEAL_IDS = new Set(["179", "434", "435", "436", "437"]);

/**
 * Canonical decimal form of a numeric deal id, whatever wire shape it arrives
 * in (number, string, padded, whitespace-wrapped). Leading zeros are stripped
 * because the deal resolver treats `0179` and `179` as the same row; anything
 * that is not a bare run of digits is not an id and resolves to "".
 */
function normalizedNumericCampaignId(value) {
  const candidate = String(value ?? "").trim();
  if (!/^\d+$/.test(candidate)) return "";
  return candidate.replace(/^0+(?=\d)/, "");
}

/**
 * Is this deal id one of the KNOWN fixed-date campaigns?
 *
 * A last-resort identity check for layers that cannot read the deal row. It
 * proves nothing about whether the campaign is still open — only the row can
 * say that — so a caller may use it to fail CLOSED when authority is missing,
 * never to declare a campaign open.
 */
export function isKnownFixedDateCampaignId(value) {
  return FIXED_DATE_CAMPAIGN_DEAL_IDS.has(normalizedNumericCampaignId(value));
}

/**
 * Whether a fixed-date campaign cohort is currently configured at all.
 *
 * Lets a campaign-scoped measure (the edge cache-boundary clamp) end itself
 * automatically when the set above is emptied, rather than becoming a
 * permanent tax that someone has to remember to remove.
 */
export function hasFixedDateCampaignCohort() {
  return FIXED_DATE_CAMPAIGN_DEAL_IDS.size > 0;
}

/** Localized, timezone-stable label for the one fixed arrival date. */
export function fixedDateVoucherArrivalLabel(deal, locale = "nl") {
  if (!isFixedDateVoucher(deal)) return null;
  const { travelStartDate: value } = resolveDealDateFields(deal);
  return localizedDateLabel(value, locale);
}

/** Localized sale/redemption deadline advertised by a fixed-date campaign. */
export function fixedDateVoucherBookingDeadlineLabel(deal, locale = "nl") {
  if (!isFixedDateVoucher(deal)) return null;
  const { bookingEndDate: value } = resolveDealDateFields(deal);
  return localizedDateLabel(value, locale);
}

function isoDatePrefix(value) {
  if (typeof value !== "string") return null;
  const match = /^(\d{4}-\d{2}-\d{2})(?:$|T)/.exec(value.trim());
  return match && parseIsoDateOnly(match[1]) != null ? match[1] : null;
}

/**
 * P12 (27-09-2026): the ABSOLUTE end date a newly bought code of this
 * flexible catalogue voucher will carry, as `YYYY-MM-DD`, or null.
 *
 * Source is the backend's `voucher_valid_until` (the governing campaign's
 * `cms_package_voucher_campaign.voucher_valid_until`). The backend gives an
 * absolute campaign date precedence over the rolling 12 months when it mints
 * the code, so whenever this is set the generic "12 maanden geldig" copy is
 * false and must be replaced by this date.
 *
 * Returns null (= keep today's copy) when:
 *   - the field is absent, empty or not a real calendar date;
 *   - the voucher is a fixed-arrival campaign: that product already has its
 *     own arrival/deadline copy, which this must not replace.
 * When both casings arrive and disagree, the EARLIER date wins: the page may
 * never promise a longer validity than the code can get.
 */
export function voucherCampaignValidUntil(deal) {
  if (!deal || typeof deal !== "object") return null;
  if (isFixedDateVoucher(deal)) return null;
  const candidates = [
    isoDatePrefix(deal.voucher_valid_until),
    isoDatePrefix(deal.voucherValidUntil),
  ].filter(Boolean);
  if (candidates.length === 0) return null;
  return candidates.sort()[0];
}

/** Localized label ("30 april 2027") for `voucherCampaignValidUntil`. */
export function voucherCampaignValidUntilLabel(deal, locale = "nl") {
  return localizedDateLabel(voucherCampaignValidUntil(deal), locale);
}

/**
 * P12 ronde 2: the campaign's month count (`voucher_validity_months`) when
 * it CHANGES what a new code gets, else null. The backend issues:
 * absolute date first, then a positive month count, then 12. So this is
 * null when there is an absolute date (that wins), for a fixed-arrival
 * campaign, for a missing/non-positive/non-integer value, and for exactly 12
 * (today's copy is then already right). Two different casings: the SMALLER
 * count wins, the page may never promise longer than the code gets.
 */
export function voucherCampaignValidityMonths(deal) {
  if (!deal || typeof deal !== "object") return null;
  if (isFixedDateVoucher(deal)) return null;
  if (voucherCampaignValidUntil(deal)) return null;
  const counts = [deal.voucher_validity_months, deal.voucherValidityMonths]
    .filter((value) => value !== null && value !== undefined && value !== "")
    .map(Number)
    .filter((value) => Number.isInteger(value) && value > 0);
  if (counts.length === 0) return null;
  const months = Math.min(...counts);
  return months === 12 ? null : months;
}

/**
 * P12 ronde 2: everything a page needs to state the real validity of a
 * newly bought code, or null when the rolling "12 maanden" copy is right.
 *   { untilIso, untilLabel, months }  (exactly one of until / months set)
 */
export function voucherCampaignValidity(deal, locale = "nl") {
  const untilIso = voucherCampaignValidUntil(deal);
  if (untilIso) {
    return {
      untilIso,
      untilLabel: localizedDateLabel(untilIso, locale),
      months: null,
    };
  }
  const months = voucherCampaignValidityMonths(deal);
  if (months) return { untilIso: null, untilLabel: null, months };
  return null;
}
